import styled from "@emotion/styled";
import { theme } from "../lib/theme";

export interface RangeToolbarProps {
  ranges: { key: string; label: string; mobileHidden?: boolean }[];
  activeRange: string | null;
  onRangeSelect: (key: string) => void;
  canGoBack: boolean;
  canGoForward: boolean;
  onGoBack: () => void;
  onGoForward: () => void;
  onGoToLatest: () => void;
  showArrows?: boolean;
  /**
   * What the range labels count, when every one of them counts the same thing — "blocks" for
   * `32 / 96 / 512`. Written once per button on a wide screen and dropped on a phone, where three
   * copies of the same noun were the difference between a toolbar that fit and one that did not.
   * Leave it off for ranges that already read on their own, like `24h / 3d / 7d`.
   */
  unit?: string;
}

export function RangeToolbar({
  ranges,
  activeRange,
  onRangeSelect,
  canGoBack,
  canGoForward,
  onGoBack,
  onGoForward,
  onGoToLatest,
  showArrows = true,
  unit,
}: RangeToolbarProps) {
  return (
    <Controls>
      <LatestButton title="Go to latest" onClick={onGoToLatest}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transform: "rotate(-90deg)" }}>
          <path d="M8 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 12l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </LatestButton>
      {showArrows && (
        <NavBtn disabled={!canGoBack} onClick={onGoBack}>
          &#x25C0;
        </NavBtn>
      )}
      <Selector>
        {ranges.map((r) => (
          <RangeBtn
            key={r.key}
            active={activeRange === r.key}
            mobileHidden={r.mobileHidden}
            onClick={() => onRangeSelect(r.key)}
            /* The unit is only hidden, never dropped, so the button still says what it selects to
               anyone not reading it off the screen. */
            aria-label={unit ? `${r.label} ${unit}` : undefined}
          >
            {r.label}
            {unit && <Unit> {unit}</Unit>}
          </RangeBtn>
        ))}
      </Selector>
      {showArrows && (
        <NavBtn disabled={!canGoForward} onClick={onGoForward}>
          &#x25B6;
        </NavBtn>
      )}
    </Controls>
  );
}

/*
 * Every button here is unshrinkable, so the toolbar is only ever as wide as its labels — 254px once
 * `Unit` drops the repeated noun on a phone, which fits the 304px a 320px screen leaves. The scroll
 * is the floor under that arithmetic rather than a thing users should ever meet: hand this component
 * a range list long enough and it pans instead of running off the page.
 *
 * The layout problem this has to solve, and the reason it is not simply `overflow-x: auto`: scrolling
 * drops this box's min-content to zero, so the wrapping `HeaderControls` that both callers put around
 * it would take that as licence to seat the live toggle alongside and squeeze the toolbar down to
 * whatever was left.
 *
 * That was previously fixed with `flex-basis: 100%` — claim the whole line, and nothing can share it.
 * It works, but it applies at every width, so on a desktop the page title and this toolbar were always
 * two lines with a half-empty row above them. Refusing to shrink says the same thing more precisely:
 * the toolbar keeps its natural width, sits beside the title whenever the row can hold both, and wraps
 * to its own line when it cannot — which is the phone case, where `max-width: 100%` then caps it and
 * the scroll takes over. One rule covers both, and neither depends on a breakpoint.
 *
 * It still needs `min-width: 0` on that `HeaderControls`, for the same reason as before.
 */
const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  /* Never squeezed: at its natural width beside the title, or wrapped onto its own line. */
  flex: 0 0 auto;
  /* The cap that turns "too wide for the line" into a scroll instead of an overflow. */
  max-width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;

  @media (max-width: 768px) {
    padding-bottom: ${theme.spacing.sm};
  }
`;

const LatestButton = styled.button`
  padding: 6px 8px;
  background: ${theme.colors.bgCard};
  color: ${theme.colors.textMuted};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
  &:hover {
    color: ${theme.colors.primary};
    background: ${theme.colors.bgHover};
  }
`;

const NavBtn = styled.button<{ disabled?: boolean }>`
  padding: 6px 10px;
  background: ${theme.colors.bgCard};
  color: ${(p) => (p.disabled ? theme.colors.border : theme.colors.textMuted)};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.sm};
  cursor: ${(p) => (p.disabled ? "default" : "pointer")};
  font-size: ${theme.fontSize.sm};
  line-height: 1;
  transition: color 0.15s, background 0.15s;
  &:hover:not(:disabled) {
    color: ${theme.colors.text};
    background: ${theme.colors.bgHover};
  }
`;

const Selector = styled.div`
  display: flex;
  gap: 2px;
  background: ${theme.colors.bgCard};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 2px;
`;

/**
 * Carries the noun the range counts. Hidden on a phone, where "32 blocks · 96 blocks · 512 blocks"
 * spent two thirds of the toolbar restating the same word — and, since the buttons never had a
 * `nowrap`, spent it on a second line of "blocks" under every number.
 */
const Unit = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const RangeBtn = styled.button<{ active: boolean; mobileHidden?: boolean }>`
  padding: 6px 12px;
  background: ${(p) => (p.active ? theme.colors.primary : "transparent")};
  color: ${(p) => (p.active ? "#fff" : theme.colors.textMuted)};
  border: none;
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  font-size: ${theme.fontSize.xs};
  font-family: monospace;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${(p) =>
      p.active ? theme.colors.primary : theme.colors.bgHover};
    color: ${theme.colors.text};
  }
  @media (max-width: 768px) {
    ${(p) => p.mobileHidden && "display: none;"}
    /* A bare "32" is a small thing to hit. Hold the button near the 44px the platforms ask for,
       which the unit's width used to provide for free. */
    min-width: 44px;
  }
`;
